import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, PieChart, Sparkles } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { EmptyState } from "@/components/dashboard/ui";

export default function LabelSplits() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/label/splits");
      setRows(data.splits || []);
    } catch (e) {
      toast.error("Failed to load splits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = Math.round(rows.reduce((sum, r) => sum + (Number(r.percent) || 0), 0) * 100) / 100;

  const setPercent = (userId, value) => {
    const v = value === "" ? 0 : Math.max(0, Math.min(100, Number(value)));
    setRows((rs) => rs.map((r) => (r.user_id === userId ? { ...r, percent: v } : r)));
  };

  const distributeEvenly = () => {
    if (rows.length === 0) return;
    const each = Math.floor((100 / rows.length) * 100) / 100;
    const next = rows.map((r) => ({ ...r, percent: each }));
    // put the remainder on the first artist so it totals exactly 100
    const remainder = Math.round((100 - each * rows.length) * 100) / 100;
    if (next[0]) next[0].percent = Math.round((next[0].percent + remainder) * 100) / 100;
    setRows(next);
  };

  const save = async () => {
    if (total !== 100) {
      toast.error(`Splits must total 100% (currently ${total}%)`);
      return;
    }
    setSaving(true);
    try {
      await api.put("/label/splits", { splits: rows.map((r) => ({ user_id: r.user_id, percent: Number(r.percent) })) });
      toast.success("Royalty splits saved");
    } catch (err) {
      toast.error(formatApiError(err, "Failed to save splits"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>;

  return (
    <div className="space-y-6 max-w-2xl" data-testid="label-splits">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl tracking-tight text-white">Royalty splits</h2>
          <p className="mt-1.5 text-slate-400">Set how your label's royalties are shared across artists. Must total 100%.</p>
        </div>
        <button data-testid="splits-distribute" onClick={distributeEvenly} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2.5 text-sm text-white hover:bg-white/5 transition-colors shrink-0">
          <Sparkles size={15} /> Even split
        </button>
      </div>

      {rows.length === 0 ? (
        <EmptyState title="No artists to split with" desc="Invite artists to your roster first, then define their royalty shares." />
      ) : (
        <>
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
            {rows.map((r) => (
              <div key={r.user_id} data-testid={`split-row-${r.user_id}`} className="flex items-center justify-between gap-4 px-5 py-4">
                <div className="min-w-0">
                  <div className="text-white truncate">{r.name}</div>
                  <div className="text-xs text-slate-500 truncate">{r.email}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <input
                    data-testid={`split-input-${r.user_id}`}
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    value={r.percent}
                    onChange={(e) => setPercent(r.user_id, e.target.value)}
                    className="w-24 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-right text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                  <span className="text-slate-400">%</span>
                </div>
              </div>
            ))}
          </div>

          <div className={`flex items-center justify-between rounded-2xl border px-5 py-4 ${total === 100 ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}>
            <div className="flex items-center gap-2 text-sm">
              <PieChart size={18} className={total === 100 ? "text-emerald-400" : "text-amber-400"} />
              <span className={total === 100 ? "text-emerald-300" : "text-amber-300"} data-testid="splits-total">
                Total: {total}%
              </span>
            </div>
            <button
              data-testid="splits-save"
              onClick={save}
              disabled={saving || total !== 100}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-black font-medium px-6 py-2.5 hover:bg-slate-200 disabled:opacity-50 transition-colors"
            >
              {saving ? <Loader2 size={18} className="animate-spin" /> : "Save splits"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
